import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { UtensilsCrossed, MapPin, CheckCircle2, Bike, Phone, Flame, Gift, Tag, Sparkles, ShoppingBag, Clock, Heart, Search, ChevronRight } from 'lucide-react';
import { gastronomiaMenu } from '../../shared/data/gastronomiaData';

export const GastronomyApp: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  const [activeTab, setActiveTab] = useState<'cardapio' | 'cupons' | 'rastreio'>('cardapio');
  const [orderStep, setOrderStep] = useState<number>(2); // 0: Confirmado, 1: Na Chapa, 2: Saiu p/ Entrega, 3: Entregue

  useEffect(() => {
    window.scrollTo(0, 0);
    const root = document.documentElement;
    root.style.setProperty('--ym-prospect-primary', '#E11D48');
    root.style.setProperty('--ym-prospect-accent', '#F59E0B');
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 font-sans text-[#0F172A]">
      <div className="w-[390px] h-[844px] bg-white rounded-[2.5rem] relative overflow-hidden border-[12px] border-slate-950 flex flex-col justify-between border-slate-800">
        
        {/* Header App Mobile */}
        <div className="flex-1 flex flex-col overflow-hidden">
           {/* Top Status Bar */}
           <div className="h-10 flex items-center justify-between px-8 text-xs font-medium pt-2 text-slate-500 shrink-0">
              <span>20:15</span>
              <div className="flex gap-1.5 items-center">
                 <div className="w-4 h-3 bg-current rounded-sm"></div>
                 <div className="w-3 h-3 bg-current rounded-full"></div>
              </div>
           </div>

           {/* Dynamic Tab Content */}
           <div className="flex-1 overflow-y-auto p-5">
              
              {/* TAB 1: CARDÁPIO & OFERTAS (LIGHT THEME) */}
              {activeTab === 'cardapio' && (
                 <div className="space-y-5">
                    <div className="flex items-center justify-between">
                       <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#E11D48] text-white flex items-center justify-center font-black">
                             <UtensilsCrossed className="w-5 h-5" />
                          </div>
                          <div>
                             <h2 className="font-heading font-black text-sm text-[#0F172A] leading-tight">{prospect.trade_name}</h2>
                             <p className="text-[10px] text-slate-500 font-bold flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-[#E11D48]" /> Entregar em {prospect.city}
                             </p>
                          </div>
                       </div>
                       <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase rounded-lg border border-emerald-200">
                          Aberto
                       </span>
                    </div>

                    {/* Banner AmoOfertas */}
                    <div className="bg-gradient-to-r from-[#E11D48] to-rose-600 text-white p-4 rounded-2xl border border-rose-500 flex justify-between items-center">
                       <div>
                          <span className="bg-white/20 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded">Oferta Relâmpago</span>
                          <h4 className="font-heading font-black text-lg mt-1">Cashback de 10%</h4>
                          <p className="text-[10px] text-rose-100">Nas compras pelo app oficial</p>
                       </div>
                       <Gift className="w-10 h-10 text-white/30" />
                    </div>

                    {/* Busca */}
                    <div className="relative">
                       <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                       <input type="text" placeholder="Buscar hambúrguer, porção..." className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-xs text-[#0F172A] outline-none" />
                    </div>

                    {/* Produtos em Destaque */}
                    <h3 className="font-heading font-black text-xs uppercase tracking-wider text-slate-400">Mais Pedidos da Semana</h3>
                    <div className="space-y-3">
                       {gastronomiaMenu.slice(0, 3).map((item) => (
                          <div key={item.id} className="bg-slate-50 border border-slate-200 p-3 rounded-2xl flex items-center gap-3">
                             <img src={item.image} className="w-16 h-16 rounded-xl object-cover border border-slate-200" alt={item.name} />
                             <div className="flex-1">
                                <h4 className="font-bold text-xs text-[#0F172A]">{item.name}</h4>
                                <p className="text-[10px] text-slate-500 line-clamp-1">{item.description}</p>
                                <span className="font-black text-xs text-[#E11D48] mt-1 block">R$ {item.price.toFixed(2)}</span>
                             </div>
                          </div>
                       ))}
                    </div>
                 </div>
              )}

              {/* TAB 2: CUPONS & CASHBACK */}
              {activeTab === 'cupons' && (
                 <div className="space-y-5">
                    <div className="flex justify-between items-center">
                       <h2 className="font-heading font-black text-base uppercase text-[#0F172A]">Sua Carteira de Cupons</h2>
                       <Sparkles className="w-5 h-5 text-amber-500" />
                    </div>

                    {/* Card de Cashback */}
                    <div className="bg-[#0F172A] text-white p-5 rounded-2xl border border-slate-800">
                       <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest block mb-1">Saldo de Cashback Disponível</span>
                       <h3 className="text-3xl font-black font-heading text-amber-400">R$ 18,50</h3>
                       <p className="text-[10px] text-slate-300 mt-2">Acumulado automaticamente em 4 pedidos nesta hamburgueria.</p>
                    </div>

                    {/* Cupons Ativos */}
                    <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-slate-400">Cupons Disponíveis</h3>
                    <div className="space-y-3">
                       <div className="bg-rose-50 border border-rose-200 p-4 rounded-2xl flex items-center justify-between">
                          <div>
                             <span className="text-[10px] font-bold text-[#E11D48] uppercase">Cupom de Boas-Vindas</span>
                             <h4 className="font-bold text-xs text-[#0F172A]">R$ 10,00 OFF no 1º Pedido</h4>
                             <p className="text-[10px] font-mono font-bold text-slate-500 mt-0.5">FIRST10</p>
                          </div>
                          <button className="bg-[#E11D48] text-white text-[10px] font-bold px-3 py-1.5 rounded-lg">Usar</button>
                       </div>

                       <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-center justify-between">
                          <div>
                             <span className="text-[10px] font-bold text-amber-700 uppercase">Frete Grátis</span>
                             <h4 className="font-bold text-xs text-[#0F172A]">Entrega Grátis acima de R$ 50</h4>
                             <p className="text-[10px] font-mono font-bold text-slate-500 mt-0.5">FRETEFREE</p>
                          </div>
                          <button className="bg-amber-600 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg">Usar</button>
                       </div>
                    </div>
                 </div>
              )}

              {/* TAB 3: RASTREIO AO VIVO DO ENTREGADOR */}
              {activeTab === 'rastreio' && (
                 <div className="space-y-5">
                    <div className="flex items-center justify-between">
                       <div>
                          <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase rounded-lg border border-emerald-200">
                             Pedido em Andamento
                          </span>
                          <h2 className="font-heading font-black text-base text-[#0F172A] mt-1">Acompanhar Entrega</h2>
                       </div>
                       <span className="text-xs font-mono font-bold text-slate-400">#1042</span>
                    </div>

                    {/* Previsão de Entrega */}
                    <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl text-center">
                       <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest block mb-1">Previsão de Chegada</span>
                       <h3 className="text-3xl font-black font-heading text-[#E11D48]">15 - 25 min</h3>
                       <p className="text-xs text-slate-600 font-medium mt-1">Entregador em deslocamento até você</p>
                    </div>

                    {/* Stepper do Pedido */}
                    <div className="space-y-4 pl-4 border-l-2 border-slate-200 relative">
                       {[
                          { label: 'Pedido Confirmado', time: '19:55', done: true },
                          { label: 'Na Chapa (Preparando)', time: '20:02', done: true },
                          { label: 'Saiu para Entrega', time: '20:12', done: true, current: true },
                          { label: 'Entregue no Endereço', time: 'Em breve', done: false },
                       ].map((step, idx) => (
                          <div key={idx} className="relative flex items-center justify-between">
                             <div className={`absolute -left-[23px] w-4 h-4 rounded-full border-2 ${step.current ? 'bg-[#E11D48] border-rose-500' : step.done ? 'bg-[#0F172A] border-slate-800' : 'bg-white border-slate-300'}`} />
                             <span className={`text-xs font-bold ${step.current ? 'text-[#E11D48]' : step.done ? 'text-[#0F172A]' : 'text-slate-400'}`}>{step.label}</span>
                             <span className="text-[10px] text-slate-400">{step.time}</span>
                          </div>
                       ))}
                    </div>

                    {/* Card do Entregador */}
                    <div className="bg-slate-100 p-4 rounded-2xl border border-slate-200 flex items-center justify-between">
                       <div className="flex items-center gap-3">
                          <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=200&auto=format&fit=crop" className="w-10 h-10 rounded-full object-cover border border-slate-300" alt="Courier" />
                          <div>
                             <h4 className="font-bold text-xs text-[#0F172A]">Mateus Oliveira</h4>
                             <p className="text-[10px] text-slate-500">Motoboy Oficial • Honda CG 160</p>
                          </div>
                       </div>
                       <button className="p-2 bg-[#E11D48] text-white rounded-xl">
                          <Phone className="w-4 h-4" />
                       </button>
                    </div>
                 </div>
              )}

           </div>
        </div>

        {/* Bottom Tab Bar (App Navigation) */}
        <div className="h-16 bg-white border-t border-slate-200 grid grid-cols-3 items-center px-4 shrink-0">
           <button 
              onClick={() => setActiveTab('cardapio')}
              className={`flex flex-col items-center gap-1 text-[10px] font-bold ${activeTab === 'cardapio' ? 'text-[#E11D48]' : 'text-slate-400'}`}
           >
              <UtensilsCrossed className="w-4 h-4" />
              <span>Cardápio</span>
           </button>
           <button 
              onClick={() => setActiveTab('cupons')}
              className={`flex flex-col items-center gap-1 text-[10px] font-bold ${activeTab === 'cupons' ? 'text-[#E11D48]' : 'text-slate-400'}`}
           >
              <Tag className="w-4 h-4" />
              <span>Cupons</span>
           </button>
           <button 
              onClick={() => setActiveTab('rastreio')}
              className={`flex flex-col items-center gap-1 text-[10px] font-bold ${activeTab === 'rastreio' ? 'text-[#E11D48]' : 'text-slate-400'}`}
           >
              <Bike className="w-4 h-4" />
              <span>Rastreio</span>
           </button>
        </div>

      </div>
    </div>
  );
};
